/**
 * FP-038: a sheet stays open until its save settles.
 *
 * Closing a dialog unmounts its form; it does not cancel the request. Nothing in
 * this app passes an AbortController, so a write sent before a dismiss lands after
 * it. On Edit Income Sources that was a Cancel that closed the sheet and then hard
 * DELETEd the row the user had removed, with the old figure still on screen.
 *
 * One shape everywhere: while a save is in flight, every way out (Cancel, the X,
 * Escape, a click outside) is held, and Save cannot be pressed twice. The sheet
 * closes on success and stays open with the edit intact on failure (FA-010), so the
 * user sees the result in the place they made the change.
 */

/**
 * Wrap a Radix `onOpenChange` so a close request is dropped while `busy`.
 *
 * Radix routes Escape, an outside pointer and its Close button through
 * `onOpenChange(false)`. The dialog is controlled, so ignoring the call leaves it
 * open. Opening is never held.
 */
export function holdDismissWhileBusy(
  busy: boolean,
  onOpenChange: ((open: boolean) => void) | undefined,
): (open: boolean) => void {
  return (open) => {
    if (!open && busy) return;
    onOpenChange?.(open);
  };
}

/**
 * One save at a time. `run(save)` reports saving true, awaits `save`, and reports
 * false when it settles either way. A second `run` while one is in flight does
 * nothing and resolves undefined: Enter in a field can submit a form whose Save
 * button is already disabled.
 */
export function createSaveGate(setSaving: (saving: boolean) => void) {
  let inFlight = false;
  return async function run<T>(save: () => Promise<T>): Promise<T | undefined> {
    if (inFlight) return undefined;
    inFlight = true;
    setSaving(true);
    try {
      return await save();
    } finally {
      inFlight = false;
      setSaving(false);
    }
  };
}
